/**
 * The TIPTAP engine (vendor `tiptap`, global `TipTap` - the build service's
 * bundled { Editor, StarterKit, TextAlign, Placeholder }).
 *
 * TipTap is headless: the toolbar here is OURS - plain buttons with Remix
 * icons on the theme tokens (scss/editor.scss), so the editor reads native
 * by construction. The extension set IS the whitelist (parsing only knows
 * registered extensions), mirroring the quill presets exactly; alignment
 * renders as the house ql-align-* CLASSES (never inline styles), so both
 * engines emit the same markup and the API's `html` whitelist stays ONE.
 */

const t = (text) => window.g.translate(text);

// command definitions: icon, label, run, active state — labels are
// THUNKS around literal t('…') calls: translation stays at render time,
// the literals stay visible to the static i18n scanner (i18n.md "The
// source scanner"; cursuri #58)
const COMMANDS = {
	bold: { icon: 'ri-bold', label: () => t('Bold'), run: (e) => e.chain().focus().toggleBold().run(), active: (e) => e.isActive('bold') },
	italic: { icon: 'ri-italic', label: () => t('Italic'), run: (e) => e.chain().focus().toggleItalic().run(), active: (e) => e.isActive('italic') },
	underline: { icon: 'ri-underline', label: () => t('Underline'), run: (e) => e.chain().focus().toggleUnderline().run(), active: (e) => e.isActive('underline') },
	strike: { icon: 'ri-strikethrough', label: () => t('Strikethrough'), run: (e) => e.chain().focus().toggleStrike().run(), active: (e) => e.isActive('strike') },
	h2: { icon: 'ri-h-2', label: () => t('Heading 2'), run: (e) => e.chain().focus().toggleHeading({ level: 2 }).run(), active: (e) => e.isActive('heading', { level: 2 }) },
	h3: { icon: 'ri-h-3', label: () => t('Heading 3'), run: (e) => e.chain().focus().toggleHeading({ level: 3 }).run(), active: (e) => e.isActive('heading', { level: 3 }) },
	h4: { icon: 'ri-h-4', label: () => t('Heading 4'), run: (e) => e.chain().focus().toggleHeading({ level: 4 }).run(), active: (e) => e.isActive('heading', { level: 4 }) },
	orderedList: { icon: 'ri-list-ordered', label: () => t('Numbered list'), run: (e) => e.chain().focus().toggleOrderedList().run(), active: (e) => e.isActive('orderedList') },
	bulletList: { icon: 'ri-list-unordered', label: () => t('Bulleted list'), run: (e) => e.chain().focus().toggleBulletList().run(), active: (e) => e.isActive('bulletList') },
	alignLeft: { icon: 'ri-align-left', label: () => t('Align left'), run: (e) => e.chain().focus().setTextAlign('left').run(), active: (e) => e.isActive({ textAlign: 'left' }) },
	alignCenter: { icon: 'ri-align-center', label: () => t('Align center'), run: (e) => e.chain().focus().setTextAlign('center').run(), active: (e) => e.isActive({ textAlign: 'center' }) },
	alignRight: { icon: 'ri-align-right', label: () => t('Align right'), run: (e) => e.chain().focus().setTextAlign('right').run(), active: (e) => e.isActive({ textAlign: 'right' }) },
	alignJustify: { icon: 'ri-align-justify', label: () => t('Justify'), run: (e) => e.chain().focus().setTextAlign('justify').run(), active: (e) => e.isActive({ textAlign: 'justify' }) },
	blockquote: { icon: 'ri-double-quotes-l', label: () => t('Quote'), run: (e) => e.chain().focus().toggleBlockquote().run(), active: (e) => e.isActive('blockquote') },
	codeBlock: { icon: 'ri-code-box-line', label: () => t('Code block'), run: (e) => e.chain().focus().toggleCodeBlock().run(), active: (e) => e.isActive('codeBlock') },
	link: { icon: 'ri-link', label: () => t('Link'), run: (e, engine) => engine.shell.linkPrompt(), active: (e) => e.isActive('link') },
	clear: { icon: 'ri-format-clear', label: () => t('Clear formatting'), run: (e) => e.chain().focus().unsetAllMarks().clearNodes().run() },
	// tables (tiptap-only — the documented per-flavor capability): the
	// context commands carry `enabled` and grey out outside a table
	table: { icon: 'ri-table-2', label: () => t('Insert table'), run: (e) => e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(), active: (e) => e.isActive('table') },
	tableRowAdd: { icon: 'ri-insert-row-bottom', label: () => t('Add row'), run: (e) => e.chain().focus().addRowAfter().run(), enabled: (e) => e.can().addRowAfter() },
	tableColAdd: { icon: 'ri-insert-column-right', label: () => t('Add column'), run: (e) => e.chain().focus().addColumnAfter().run(), enabled: (e) => e.can().addColumnAfter() },
	tableRowDel: { icon: 'ri-delete-row', label: () => t('Delete row'), run: (e) => e.chain().focus().deleteRow().run(), enabled: (e) => e.can().deleteRow() },
	tableColDel: { icon: 'ri-delete-column', label: () => t('Delete column'), run: (e) => e.chain().focus().deleteColumn().run(), enabled: (e) => e.can().deleteColumn() },
	tableDel: { icon: 'ri-close-circle-line', label: () => t('Delete table'), run: (e) => e.chain().focus().deleteTable().run(), enabled: (e) => e.can().deleteTable() },
};

const PRESETS = {
	basic: [
		['bold', 'italic', 'underline'],
		['orderedList', 'bulletList'],
		['link', 'clear'],
	],
	full: [
		['h2', 'h3', 'h4'],
		['bold', 'italic', 'underline', 'strike'],
		['orderedList', 'bulletList'],
		['alignLeft', 'alignCenter', 'alignRight', 'alignJustify'],
		['blockquote', 'codeBlock'],
		['table', 'tableRowAdd', 'tableColAdd', 'tableRowDel', 'tableColDel', 'tableDel'],
		['link', 'clear'],
	],
};

// TextAlign rendered as the house classes (the ONE whitelist): left = the
// default (no class), center/right/justify = ql-align-<v>; parsing accepts
// both the classes and plain text-align styles (pasted content).
function alignExtension(TextAlign) {
	return TextAlign.extend({
		addGlobalAttributes() {
			return [{
				types: this.options.types,
				attributes: {
					textAlign: {
						default: null,
						parseHTML: (element) => {
							const cls = (element.getAttribute('class') || '').match(/ql-align-(center|right|justify)/);
							if (cls) return cls[1];
							const style = element.style.textAlign;
							return style && style !== 'left' ? style : null;
						},
						renderHTML: (attributes) => {
							if (!attributes.textAlign || attributes.textAlign === 'left') return {};
							return { class: 'ql-align-' + attributes.textAlign };
						},
					},
				},
			}];
		},
	}).configure({ types: ['heading', 'paragraph'] });
}

// Tables rendered as the house dialect: plain <table><tbody> — no colgroup,
// no min-width styles (tiptap's defaults, resize plumbing we do not use),
// and span attributes only when > 1. What the engine emits IS what the API
// whitelist stores — the one-dialect law, table edition.
function tableExtensions({ Table, TableRow, TableHeader, TableCell }) {
	const cellRender = (tag) => ({ HTMLAttributes }) => {
		const attrs = {};
		for (const key of ['colspan', 'rowspan']) {
			if (Number(HTMLAttributes[key]) > 1) attrs[key] = HTMLAttributes[key];
		}
		return [tag, attrs, 0];
	};
	return [
		Table.configure({ resizable: false }).extend({
			renderHTML() {
				return ['table', ['tbody', 0]];
			},
		}),
		TableRow,
		TableHeader.extend({ renderHTML: cellRender('th') }),
		TableCell.extend({ renderHTML: cellRender('td') }),
	];
}

export class TiptapEngine {
	constructor(area, opts, shell) {
		this.shell = shell;
		const { Editor, StarterKit, TextAlign, Placeholder } = window.TipTap;
		const preset = PRESETS[opts.toolbar] ? opts.toolbar : 'basic';

		this.bar = document.createElement('div');
		this.bar.className = 'gedit-toolbar';
		this.bar.setAttribute('role', 'toolbar');
		area.appendChild(this.bar);
		const content = document.createElement('div');
		content.className = 'gedit-content';
		area.appendChild(content);

		// the registered extensions ARE the paste whitelist - mirror quill's
		// presets exactly (no inline code, no hr in either preset); tables
		// ride the FULL preset only, tiptap-only (the per-flavor capability
		// the contract allows — quill has no sane tables), rendered as the
		// house dialect (tableExtensions above).
		const extensions = [
			StarterKit.configure({
				link: { openOnClick: false },
				code: false,
				horizontalRule: false,
				...(preset === 'basic'
					? { heading: false, blockquote: false, codeBlock: false, strike: false }
					: { heading: { levels: [2, 3, 4] } }),
			}),
		];
		if (preset === 'full') {
			extensions.push(alignExtension(TextAlign));
			extensions.push(...tableExtensions(window.TipTap));
		}
		if (opts.placeholder) extensions.push(Placeholder.configure({ placeholder: opts.placeholder }));

		this.editor = new Editor({
			element: content,
			extensions,
			onUpdate: () => shell.onUserEdit(), // setContent passes emitUpdate: false
			onFocus: () => shell.onFocus(),
			onBlur: () => shell.onBlur(),
			onTransaction: () => this.refreshToolbar(),
		});
		if (opts.height) this.editor.view.dom.style.minHeight = opts.height + 'px';

		this.buttons = [];
		this.buildToolbar(preset);
	}

	buildToolbar(preset) {
		for (const group of PRESETS[preset]) {
			const span = document.createElement('span');
			span.className = 'gedit-group';
			for (const name of group) {
				const def = COMMANDS[name];
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.className = 'gedit-btn';
				btn.setAttribute('title', def.label());
				btn.setAttribute('aria-label', def.label());
				const icon = document.createElement('i');
				icon.className = def.icon;
				btn.appendChild(icon);
				btn.addEventListener('click', () => def.run(this.editor, this));
				if (def.active || def.enabled) this.buttons.push([btn, def]);
				span.appendChild(btn);
			}
			this.bar.appendChild(span);
		}
		this.refreshToolbar();
	}

	refreshToolbar() {
		if (!this.buttons || !this.editor.isEditable) return; // enable() owns disabled state when read-only
		for (const [btn, def] of this.buttons) {
			if (def.active) btn.classList.toggle('active', def.active(this.editor));
			if (def.enabled) btn.disabled = !def.enabled(this.editor);
		}
	}

	isEmpty() {
		if (!this.editor.isEmpty) return false;
		// text-empty is not empty when the doc holds STRUCTURE — a freshly
		// inserted (still unfilled) table is content, not an empty field
		let structural = false;
		this.editor.state.doc.descendants((node) => {
			if (node.type.name === 'table') structural = true;
			return !structural;
		});
		return !structural;
	}

	html() {
		return this.editor.getHTML();
	}

	text() {
		return this.editor.getText({ blockSeparator: '\n' }).replace(/\n+$/, '');
	}

	setHTML(html) {
		this.editor.commands.setContent(html || '', { emitUpdate: false });
	}

	enable(on) {
		this.editor.setEditable(on);
		for (const btn of this.bar.querySelectorAll('button')) btn.disabled = !on;
		if (on) this.refreshToolbar(); // re-derive the context-dependent (enabled) buttons
	}

	focus() {
		this.editor.commands.focus();
	}

	linkState() {
		return {
			href: this.editor.getAttributes('link').href || '',
			collapsed: this.editor.state.selection.empty,
		};
	}

	// Empty url removes the link; a collapsed selection outside a link
	// inserts the url as its own linked text.
	applyLink(url, state) {
		if (!url) {
			this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
		} else if (state.collapsed && !state.href) {
			this.editor.chain().focus().insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: url } }] }).run();
		} else {
			this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
		}
	}

	destroy() {
		this.editor.destroy();
	}
}
